// field-management-options.ts
import { Change, nothingHere } from "@adornis/base/change.js";
import { AdornisEntity } from "@adornis/baseql/entities/adornisEntity.js";
import { baseqlMetaData } from "@adornis/baseql/metadata/metaDataStore.js";
import { getAllFieldDefinitions } from "@adornis/baseql/metadata/resolve.js";
import { translateFieldName } from "./translate-field.js";
function hitlistHelper(hitlist, mode) {
  return (fieldDef, path, baseEntity) => {
    const doesHit = hitlist.some(
      (item) => (item instanceof RegExp ? item : new RegExp(`^${item}`)).test(path.concat(fieldDef.name).join("."))
    );
    if (mode === "blacklist")
      return !doesHit;
    if (mode === "whitelist")
      return doesHit;
    throw new Error(`Unknown mode ${mode}`);
  };
}
var shouldIncludeFieldHelpers = {
  bySelectionSet(options) {
    return (baseQLFieldDefinition, path, baseEntity) => {
      if (options.whitelist) {
        const whitelistOnThisLevel = Change.resolvePath(options.whitelist, path);
        if (whitelistOnThisLevel !== nothingHere) {
          return baseQLFieldDefinition.name in whitelistOnThisLevel;
        }
      }
      if (options.blacklist) {
        const blacklistOnThisLevel = Change.resolvePath(options.blacklist, path);
        if (blacklistOnThisLevel !== nothingHere) {
          return !(blacklistOnThisLevel[baseQLFieldDefinition.name] === 1);
        }
      }
      return true;
    };
  },
  byPathBlacklist(blacklist) {
    return hitlistHelper(blacklist, "blacklist");
  },
  byPathWhitelist(whitelist) {
    return hitlistHelper(whitelist, "whitelist");
  }
};
function searchFilter(searchString, contents) {
  return contents.some((c) => c?.toLowerCase().includes(searchString.toLowerCase()));
}
function getAvailableFields(type, path, options) {
  let fields = getAllFieldDefinitions(type);
  fields = fields.reduce(
    (acc, curr) => acc.concat(acc.some((f) => f.name === curr.name) ? [] : [curr]),
    []
  );
  if ((options.whitelist || options.blacklist) && options.shouldIncludeField)
    throw new Error("please decide, whitelist/blacklist or shouldIncludeField function");
  const shouldIncludeFn = options.shouldIncludeField ?? shouldIncludeFieldHelpers.bySelectionSet(options);
  fields = fields.filter((field) => {
    if (options.hideFieldsWithoutTranslation && translateFieldName([...path ?? [], field.name]).includes("field.")) {
      return false;
    }
    return shouldIncludeFn(field, path, type);
  });
  return fields;
}
function hasAvailableFieldsWithSearch(type, search, path, options, visited = /* @__PURE__ */ new Set()) {
  const fields = getAvailableFields(type, path, options);
  return fields.some((field) => isFieldRecursivelyMatching(field, search, path, options, visited));
}
function isFieldMatching(field, search, path) {
  return searchFilter(search, [field.name, translateFieldName([...path ?? [], field.name])]);
}
function isFieldRecursivelyMatching(field, search, path, options, visited = /* @__PURE__ */ new Set()) {
  if (visited.has(field))
    return false;
  visited.add(field);
  if (isFieldMatching(field, search, path))
    return true;
  const fieldType = field.type();
  if (!fieldType || !baseqlMetaData.entities.validSubclasses(AdornisEntity).includes(fieldType._class))
    return false;
  const result = hasAvailableFieldsWithSearch(fieldType, search, [...path, field.name], options, visited);
  return result;
}
function isFieldAlreadyMatched(type, search, path) {
  for (let i = 0; i < path.length; i++) {
    const fieldName = path[i];
    if (searchFilter(search, [fieldName, translateFieldName([...path.slice(0, i), fieldName])]))
      return true;
  }
  return false;
}
var buildFullFieldManagementOptions = (options) => {
  return {
    ...options,
    selected: [...options.selected ?? []],
    // spread to avoid writing back directly
    extraFields: [...options.extraFields ?? []],
    getAvailableFieldsWithSearch(type, path, search) {
      if (!search)
        return getAvailableFields(type ?? options.class, path ?? [], this);
      if ((type || options.class) && isFieldAlreadyMatched(type ?? options.class, search, path ?? []))
        return getAvailableFields(type ?? options.class, path ?? [], this);
      const fields = getAvailableFields(type ?? options.class, path ?? [], this);
      const filteredFields = fields.filter((field) => isFieldRecursivelyMatching(field, search, path ?? [], this));
      return filteredFields;
    }
  };
};
export {
  buildFullFieldManagementOptions,
  shouldIncludeFieldHelpers
};
